/**
 * Where the toast region must stay clear of an open sheet, so a toast never
 * covers a sheet's footer actions (DEVIATIONS.md, L-10).
 *
 * Drawers are URL-routed and mounted by `CurrentDrawer`, far from the toast
 * region, and their widths come from the drawer recipe's named sizes, so the
 * region measures the open sheet rather than being told its width: every
 * sheet's positioner carries `data-layer="sheet"` (`ui/drawer.tsx`).
 *
 * - Beside: with room to the sheet's left (at least `MIN_ROOM_PX`), the region
 *   moves to that side, a page gutter from the sheet and from the window.
 * - Above: without that room (a phone, a 70% drawer at 1024), the region stays
 *   at the bottom but rises above the sheet's footer (or its bottom row of
 *   buttons); the toast may sit over the sheet's body, never its actions.
 */
import { useEffect, useState } from "react";

/** Below this much room to a sheet's left, toasts rise above its footer. */
const MIN_ROOM_PX = 320;
/** How near the sheet's bottom a row of buttons counts as its actions. */
const ACTION_ROW_PX = 96;

export type SheetClearance =
  | { kind: "beside"; inlineEnd: number; room: number }
  | { kind: "above"; bottom: number };

function actionsTop(sheet: HTMLElement, box: DOMRect): number | null {
  // Chakra's drawer footer is its own slot (no Zag part): its slot class.
  const footer = sheet.querySelector<HTMLElement>(
    ".chakra-drawer__footer, [data-part='footer']",
  );
  if (footer) return footer.getBoundingClientRect().top;
  let top: number | null = null;
  for (const button of Array.from(sheet.querySelectorAll("button"))) {
    const rect = button.getBoundingClientRect();
    if (rect.height === 0 || box.bottom - rect.bottom > ACTION_ROW_PX) continue;
    top = Math.min(top ?? rect.top, rect.top);
  }
  return top;
}

function measureClearance(): SheetClearance | null {
  const viewport = window.innerWidth;
  const sheets = document.querySelectorAll<HTMLElement>(
    "[data-layer='sheet'] [data-part='content'][data-state='open']",
  );
  let beside: SheetClearance | null = null;
  let above: SheetClearance | null = null;
  for (const sheet of Array.from(sheets)) {
    const box = sheet.getBoundingClientRect();
    // Only a sheet reaching the bottom right sits where the toasts do.
    if (box.width === 0 || viewport - box.right > box.width) continue;
    if (box.left >= MIN_ROOM_PX) {
      const inlineEnd = viewport - box.left;
      if (beside?.kind !== "beside" || inlineEnd > beside.inlineEnd)
        beside = { kind: "beside", inlineEnd, room: box.left };
      continue;
    }
    const top = actionsTop(sheet, box);
    if (top == null) continue;
    const bottom = window.innerHeight - top;
    if (above?.kind !== "above" || bottom > above.bottom)
      above = { kind: "above", bottom };
  }
  return above ?? beside;
}

export function useOpenSheetClearance(): SheetClearance | null {
  const [clearance, setClearance] = useState<SheetClearance | null>(null);

  useEffect(() => {
    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const next = measureClearance();
        setClearance((current) =>
          JSON.stringify(current) === JSON.stringify(next) ? current : next,
        );
      });
    };
    const observer = new MutationObserver(update);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-state", "style"],
    });
    window.addEventListener("resize", update);
    update();
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  return clearance;
}
